import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components_catalyst/button';
import { Field, Label, ErrorMessage } from '../components_catalyst/fieldset';
import { Input } from '../components_catalyst/input';
import { useAuth } from './AuthContext';
import { useDispatch } from 'react-redux';
import { loadCurrentUserProfile } from '../store/map3DataSlice';
import { AppDispatch } from '../store/store';
import { GoogleAuthButton } from './GoogleAuthButton';
import { readStoredExternalAuthId } from './oauthConnect';

type SignInMode = 'select' | 'email';

/**
 * SignIn component
 *
 * This component is used to sign in a user. If a user was interrupted after initiating signup,
 * before completing their user profile (specifically, saving a userName), this will be detected and the user will be redirected to
 * the complete-signup page before completing the standard signing and redirect flow.
 *
 * @returns {JSX.Element} The SignIn component
 */
export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<SignInMode>('select');
  const navigate = useNavigate();
  const auth = getAuth();
  const { signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const signUpComplete = searchParams.get('signUpComplete');

  const loadCurrentUser_ = async () => {
    try {
      const result = await dispatch(loadCurrentUserProfile()).unwrap();
      if (!result.userName) {
        navigate('/complete-signup');
      } else if (readStoredExternalAuthId()) {
        // An AI app is mid-connect (OAuth login bridge): return to it instead of the profile page.
        navigate('/oauth/connect', { replace: true });
      } else {
        navigate(`/${result.userName}`);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load user profile');
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      await loadCurrentUser_();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <h1 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Sign in
        </h1>
        <p className="text-gray-500 text-center mt-2">
          Don't have an account?{' '}
          <Link to="/sign-up" className="text-blue-600 hover:text-blue-800">
            Sign up
          </Link>
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        {signUpComplete && (
          <div
            className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800 mb-6"
            role="status"
            aria-live="polite"
          >
            <p className="mb-2">Your account has successfully been created. Please sign in.</p>
            <p>
              Note: MilestoneMap.org is currently in a closed alpha release. You need an invite to
              create or use milestone maps here. To join the waiting list or get a copy of the open
              Creative Commons template, visit{' '}
              <a
                href="https://milestonemap.org/request-invite"
                className="underline hover:text-blue-800"
                target="_blank"
                rel="noopener noreferrer"
              >
                milestonemap.org/request-invite
              </a>
              .
            </p>
          </div>
        )}

        {error && (
          <Field role="alert" aria-live="assertive" className="mb-4 text-center">
            <ErrorMessage>{error}</ErrorMessage>
          </Field>
        )}

        {mode === 'select' && (
          <div className="space-y-4" role="group" aria-label="Sign in options">
            <GoogleAuthButton
              mode="signin"
              onSuccess={loadCurrentUser_}
              onError={(err) => setError(err.message)}
              disabled={isLoading}
            />
            <Button
              onClick={() => setMode('email')}
              className="w-full"
              aria-disabled={isLoading}
              disabled={isLoading}
            >
              Sign in with Email
            </Button>
          </div>
        )}

        {mode === 'email' && (
          <form
            onSubmit={handleEmailSignIn}
            className="space-y-6"
            aria-label="Email sign in form"
            noValidate
          >
            <Button
              plain
              onClick={() => setMode('select')}
              className="mb-4"
              aria-label="Back to sign in options"
            >
              ← Back
            </Button>

            <Field>
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-required="true"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'signin-error' : undefined}
                disabled={isLoading}
              />
            </Field>

            <Field>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                aria-required="true"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'signin-error' : undefined}
                disabled={isLoading}
              />
            </Field>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              aria-disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
